import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { SimpleTable } from '../k2/K2Tables';
import { fmtKc } from '../k2/k2utils';
import { fmtQty } from '../k2/itemsUtils';
import { rankArticles } from './analyticsUtils';
import { Panel, SearchBox, TopBars } from './PartnerRanking';
import { LinesDialog } from './LinesDialog';

const Pct = ({ v }) => (v == null ? <span className="text-[#94A3B8]">—</span> : (
  <span className={v > 0 ? 'text-[#DC2626]' : 'text-[#16A34A]'}>{v >= 0 ? '+' : ''}{v.toFixed(1)} %</span>
));
const priceFmt = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 2 });
const up = (v, u) => (v == null ? '—' : `${priceFmt.format(v)} Kč/${u || 'MJ'}`);

function columns(withPrev, showPrices) {
  const cols = [
    { key: 'rank', label: '#', className: 'text-[#94A3B8] tabular-nums' },
    { key: 'code', label: 'Kód', className: 'whitespace-nowrap' },
    { key: 'name', label: 'Zboží', className: 'font-medium' },
    { key: 'qty', label: 'Množství', num: true, render: (r) => fmtQty(r.qty, r.unit) },
    { key: 'value', label: 'Hodnota', num: true, render: (r) => fmtKc(r.value), className: 'font-semibold' },
  ];
  if (showPrices) {
    cols.push({ key: 'avg', label: 'Prům. cena', num: true, render: (r) => up(r.avg, r.unit) });
    cols.push({ key: 'range', label: 'Min – max', num: true, render: (r) => (r.min == null ? '—' : `${priceFmt.format(r.min)} – ${priceFmt.format(r.max)}`), className: 'text-[#57534E]' });
    if (withPrev) {
      cols.push({ key: 'prevAvg', label: 'Předch. cena', num: true, render: (r) => up(r.prevAvg, r.unit), className: 'text-[#57534E]' });
      cols.push({ key: 'priceDelta', label: 'Změna ceny', num: true, render: (r) => <Pct v={r.priceDelta} /> });
    }
  }
  cols.push({ key: 'docCount', label: 'Dokladů', num: true });
  return cols;
}

export const ArticleRanking = ({ items, loading, error, range, withPrev, showPrices, docsByRid, partnerLabel }) => {
  const [metric, setMetric] = useState('value');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const all = useMemo(() => rankArticles(items, range, withPrev), [items, range, withPrev]);
  const rows = useMemo(() => [...all].sort((a, b) => b[metric] - a[metric]).map((r, i) => ({ ...r, rank: i + 1 }))
    .filter((r) => !q || `${r.code} ${r.name}`.toLowerCase().includes(q.toLowerCase())), [all, metric, q]);

  const toggle = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex gap-1 p-1 bg-[#F1F5F1] rounded-xl">
        {[{ id: 'value', label: 'Hodnota Kč' }, { id: 'qty', label: 'Množství' }].map((m) => (
          <button key={m.id} type="button" onClick={() => setMetric(m.id)} data-testid={`an-articles-metric-${m.id}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${metric === m.id ? 'bg-white text-[#16A34A] shadow-sm' : 'text-[#57534E] hover:text-[#16A34A]'}`}>{m.label}</button>
        ))}
      </div>
      <SearchBox value={q} onChange={setQ} testId="an-articles-search" placeholder="Hledat zboží…" />
    </div>
  );
  return (
    <Panel testId="an-articles" right={toggle}
      title={<span className="flex items-center gap-2">Žebříček zboží {loading && <Loader2 className="w-4 h-4 animate-spin text-[#16A34A]" data-testid="an-articles-loading" />}</span>}
      subtitle={`${all.length} druhů zboží · kliknutím zobrazíte jednotlivé řádky${showPrices ? ' · ceny = skladová cena / MJ' : ''}`}>
      {error && <div className="p-3 rounded-xl bg-[#FEE2E2] text-[#991B1B] text-sm" data-testid="an-articles-error">{error}</div>}
      <div className={`space-y-4 transition-opacity ${loading ? 'opacity-50' : ''}`}>
        {!q && rows.length > 0 && (
          <TopBars testId="an-articles-chart" valueKey="v" color="#0EA5E9"
            data={rows.slice(0, 10).map((r) => ({ label: r.name.length > 40 ? `${r.name.slice(0, 40)}…` : r.name, v: r[metric], unit: r.unit }))}
            fmt={(v, p) => (metric === 'qty' ? fmtQty(v, p.unit) : fmtKc(v))} />
        )}
        <SimpleTable key={`${metric}-${q}`} testId="an-articles-table" columns={columns(withPrev, showPrices)} rows={rows} rowKey={(r) => r.code || r.name} onRowClick={setOpen} />
      </div>
      <LinesDialog title={open ? `${open.name} (${open.code})` : ''} lines={open?.lines || []} open={!!open} onClose={() => setOpen(null)}
        docsByRid={docsByRid} partnerLabel={partnerLabel} testId="an-article-detail" />
    </Panel>
  );
};
